import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';

import { publicDataDomain } from '../../../util/config';
import { setCurrentMediaId, setMediasData, setMediaType, setRelatedMediaOfScene, setScenario, setSceneConfig, setRestrictedMedias } from '../../../actions/configurationActions';
import { setCurrentSceneWidgets } from '../../../actions/playerActions';

import { FetchDataHelper } from './fetch-data-helper';

interface UseFetchInteractProjectDataProps {
	scenarioId: string;
	accountId: string;
	isUsedOnMobile: boolean;
	fromSceneEditor: boolean;
	setLoading?: (_value: boolean) => void;
	updateAndroidInteractOverlayPosition?: () => void;
	getRestrictToken: () => string;
	tokens: string[] | null;
	queryParams: any;
}

const useFetchInteractProjectData = ({ scenarioId, accountId, isUsedOnMobile, fromSceneEditor, setLoading, updateAndroidInteractOverlayPosition, getRestrictToken, tokens, queryParams }: UseFetchInteractProjectDataProps) => {
	const dispatch = useDispatch();

	const { scenario, sceneConfig, restrictedToken, restrictedMedias } = useSelector((state) => (state as any).configurationReducer);

	const [interactProjectData, setInteractProjectData] = useState<any>();
	const [processedData, setProcessedData] = useState<any>();

	const getProcessedInteractProjectData = (dataParam: any | undefined, sceneConfigParam: any | undefined, isFirstTime: boolean | undefined) => {
		const data = dataParam ?? scenario;
		if (!data) {
			return;
		}
		const currentSceneId = sceneConfigParam === undefined ? queryParams?.sceneId : sceneConfigParam?.currentScene;
		const processedData = FetchDataHelper.getScenesData({
			scenario: data,
			isUsedOnMobile,
			fromSceneEditor,
			isFirstTime: isFirstTime,
			sceneId: currentSceneId ?? sceneConfigParam?.currentScene,
		});

		processedData && setProcessedData(processedData);
		processedData?.currentMediaId && dispatch(setCurrentMediaId(processedData?.currentMediaId));
		processedData?.sceneConfig && dispatch(setSceneConfig(processedData?.sceneConfig));
		processedData?.widgetsWithTime && dispatch(setCurrentSceneWidgets(processedData.widgetsWithTime));

		if (isFirstTime) {
			setInteractProjectData(data);
			data.defaultSceneBGMediaId = processedData?.defaultSceneBGMediaId ?? "";
			dispatch(setScenario(data));

			processedData?.relatedSceneMedias && dispatch(setRelatedMediaOfScene(processedData.relatedSceneMedias));
			if (processedData?.mediasToLoad && processedData?.mediasToLoad.length > 0) {
				const mediasData = processedData?.mediasToLoad.filter(Boolean).map((mediaToLoad: any) => FetchDataHelper.getMediaData({
					mediaData: { ...mediaToLoad, id: mediaToLoad.id }, id: mediaToLoad.id, sceneId: mediaToLoad.sceneId
				}))
				dispatch(setMediasData(mediasData));
			}
		}
	};

	useEffect(() => {
		if (fromSceneEditor) {
			return;
		}
		getProcessedInteractProjectData(undefined, sceneConfig, false);
		updateAndroidInteractOverlayPosition?.()
	}, [dispatch, sceneConfig?.currentScene, sceneConfig?.nextScene, fromSceneEditor, updateAndroidInteractOverlayPosition]);

	useEffect(() => {
		if (scenario) {
			return;
		}

		// Check tokens to make sure only fetch data when received tokens - avoid Password Screen flashing issue (only when useLoader is true)
		const urlParams = new URLSearchParams(window.location.search);
		const useLoader = urlParams.get('useLoader');

		if (!scenarioId || !accountId || (useLoader === "true" && tokens === null)) {
			return;
		}
		const scenarioUrl = `${publicDataDomain}${accountId}/scenarios/${scenarioId}`;
		axios.get(scenarioUrl, {
			headers: {
				'Content-Type': 'application/json',
				'X-Authorization': 'AVP1 ' + getRestrictToken(),
			}
		}).then((response) => {
			dispatch(setMediaType('scenario'));
			const newRestrictedMedias = restrictedMedias.filter((screen: string) => screen !== "scenario");
			dispatch(setRestrictedMedias(newRestrictedMedias));

			if (response?.data) {
				getProcessedInteractProjectData(response.data, undefined, true);
			}
			setLoading?.(false);
		}).catch((error) => {
			if (error.response?.data?.token === true) {
				dispatch(setRestrictedMedias([...restrictedMedias, "scenario"]));
			}
			setLoading?.(false);
		});
	}, [accountId, dispatch, scenarioId, setLoading, scenario, restrictedToken, getRestrictToken, tokens]);

	return { interactProjectData, ...processedData };
};

export default useFetchInteractProjectData;
